import { useEffect, useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { EmptyState } from '../components/EmptyState';
import { compareBy, useUrlClear, useUrlNumber, useUrlSort, useUrlState } from '../lib/useUrlState';
import { timeAgo } from '../lib/agentCommands';
import { formatDate, formatDateTime } from '../lib/format';

/**
 * Network → Peers. Everyone and everything holding WireGuard credentials.
 *
 * Ported from optisigns-staff-app (`features/network/EmployeePeersPage` +
 * `PeersTableBySubnet` + `DevicesTable`). READ-ONLY: peer create/delete lives in
 * staff-app's enrollment path, not here.
 *
 * Two tables, because the two peer kinds page differently:
 *   - employee peers — small (staff laptops), fetched flat and grouped by subnet
 *     client-side.
 *   - device peers — signage fleet, SERVER-paginated + searchable.
 */

const VPN_EMPLOYEE_PEERS = gql`
  query VpnEmployeePeers {
    vpnEmployeePeers {
      _id
      name
      ownerEmail
      vpnIp
      subnet
      region
      createdByRole
      createdAt
      lastHandshakeAt
      kind
    }
  }
`;

const VPN_DEVICE_PEERS = gql`
  query VpnDevicePeers($filter: VpnDevicePeerFilterInput, $pagination: VpnPaginationInput) {
    vpnDevicePeers(filter: $filter, pagination: $pagination) {
      total
      items {
        _id
        name
        vpnIp
        region
        createdAt
        lastHandshakeAt
        deviceName
        accountId
      }
    }
  }
`;

interface Peer {
  _id: string;
  name: string;
  ownerEmail?: string | null;
  vpnIp: string;
  subnet: string;
  region: string;
  createdByRole: string;
  createdAt: string;
  lastHandshakeAt?: string | null;
  kind?: string | null;
  deviceName?: string | null;
  accountId?: string | null;
}

// Rows per page within each subnet group (client-side slice of the flat list).
const PEERS_PER_PAGE = 20;

// Liveness from the WireGuard handshake (mdm-devices.lastHandshakeAt, written by
// wg-orchestrator's handshake-sync ~every 2 min from the agent's 25s keepalive).
// This — not updatedAt — is the real "is this machine online" signal.
// online: <3 min · stale: 3–15 min · offline: older / never.
const ONLINE_MS = 3 * 60 * 1000;
const STALE_MS = 15 * 60 * 1000;

type Liveness = 'online' | 'stale' | 'offline';

export function liveness(lastHandshakeAt?: string | null): Liveness {
  if (!lastHandshakeAt) return 'offline';
  const age = Date.now() - new Date(lastHandshakeAt).getTime();
  if (Number.isNaN(age)) return 'offline';
  if (age < ONLINE_MS) return 'online';
  if (age < STALE_MS) return 'stale';
  return 'offline';
}

// Map liveness → the shared health-dot colours. `offline` uses the neutral grey
// (`none`), not red: a laptop being off is normal, not a fault.
const DOT: Record<Liveness, string> = { online: 'ok', stale: 'warn', offline: 'none' };
const DOT_LABEL: Record<Liveness, string> = {
  online: 'Online',
  stale: 'Stale',
  offline: 'Offline',
};

function Heartbeat({ at }: { at?: string | null }) {
  const lvl = liveness(at);
  return (
    <span
      className="dot-cell"
      title={at ? `${DOT_LABEL[lvl]} · ${formatDateTime(at)}` : DOT_LABEL[lvl]}
    >
      <span className={`health-dot ${DOT[lvl]}`} />
      <span><span className="peer-status">{DOT_LABEL[lvl]}</span><span className="muted text-xs peer-heartbeat-time">{at ? timeAgo(at) : 'No handshake yet'}</span></span>
    </span>
  );
}

/**
 * Peer `name` is `"<owner-email>:<hostname>"` for staff peers. The owner has its
 * own column, so showing the whole thing duplicates it and makes the widest
 * column ~60 chars. Render just the machine part; the full value stays in `title`.
 */
function machineName(p: Peer): string {
  const i = p.name.indexOf(':');
  return i > 0 ? p.name.slice(i + 1) : p.name;
}

/** Sortable columns of the employee-peer table → the scalar behind the cell.
 *  Heartbeat sorts on the timestamp, so "who has been dark longest" is one click
 *  — a peer that never handshook sorts last either way (compareBy puts nullish
 *  at the bottom regardless of direction). */
const SORT_PICK: Record<string, (p: Peer) => unknown> = {
  machine: machineName,
  ownerEmail: (p) => p.ownerEmail,
  vpnIp: (p) => p.vpnIp,
  region: (p) => p.region,
  lastHandshakeAt: (p) => p.lastHandshakeAt,
  createdAt: (p) => p.createdAt,
};

// Same shape as every other page's Stat (ClustersPage / SecretsPage / …). The
// `.label` + `.value` classes are what globals.css styles: block-level so they
// stack, uppercase xs label over an fs-xl tabular-nums value. Hand-rolling inline
// spans here collapses them onto one line ("Employee peers81") and loses all of it.
function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

export function VpnPeersPage() {
  const { data, loading, error } = useQuery<{ vpnEmployeePeers: Peer[] }>(VPN_EMPLOYEE_PEERS, {
    fetchPolicy: 'cache-and-network',
  });
  // Employee-peer filters + sort in the URL. The device table below has its own
  // `d*` keys because the two tables are independent and share one address bar.
  const [q, setQ] = useUrlState('q');
  const [subnetFilter, setSubnetFilter] = useUrlState('subnet');
  const clearFilters = useUrlClear(['q', 'subnet']);
  const sort = useUrlSort('ownerEmail');

  const peers = data?.vpnEmployeePeers ?? [];
  const subnetNames = useMemo(
    () => [...new Set(peers.map((p) => p.subnet))].sort(),
    [peers],
  );

  const needle = q.trim().toLowerCase();
  const matching = useMemo(
    () =>
      peers.filter(
        (p) =>
          (!needle ||
            p.name.toLowerCase().includes(needle) ||
            (p.ownerEmail ?? '').toLowerCase().includes(needle) ||
            p.vpnIp.toLowerCase().includes(needle)) &&
          (!subnetFilter || p.subnet === subnetFilter),
      ),
    [peers, needle, subnetFilter],
  );

  // Grouping is client-side by design — the backend returns a flat list.
  // The default sort is by OWNER: people commonly hold several laptops, and
  // Mongo's natural order scatters them, which makes "how many machines does X
  // have?" — the main question on this page — hard to answer.
  const groups = useMemo(() => {
    const map = new Map<string, Peer[]>();
    for (const p of matching) {
      const arr = map.get(p.subnet) ?? [];
      arr.push(p);
      map.set(p.subnet, arr);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [matching]);

  const onlineCount = peers.filter((p) => liveness(p.lastHandshakeAt) === 'online').length;
  const anyFilter = Boolean(needle || subnetFilter);

  if (loading && !data)
    return (
      <div className="page page--wide operations-page peers-page">
        <div className="skeleton-row-group">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      </div>
    );
  if (error)
    return (
      <div className="page page--wide operations-page peers-page">
        <p className="error">Couldn&apos;t load the peers — the API didn&apos;t respond.</p>
        <p className="muted">
          The VPN itself is unaffected; this view only reads. If it persists, ask in #devops-infra.
        </p>
      </div>
    );

  return (
    <div className="page page--wide operations-page peers-page">
      <header className="operations-heading"><div><h1>VPN peers</h1><p>Monitor employee and device connections across the WireGuard network.</p></div></header>
      <p className="operations-note">Enrollment is managed through OptiEdge. Online: handshake within 3 minutes · Stale: 3–15 minutes · Offline: over 15 minutes.</p>
      <div className="stat-row">
        <Stat label="Employee peers" value={peers.length} />
        <Stat label="Online now" value={`${onlineCount} / ${peers.length}`} />
        <Stat label="Subnets in use" value={subnetNames.length} />
      </div>

      <div className="filter-row">
        <input
          type="search"
          aria-label="Search employee peers" placeholder="Search machine, owner, or VPN IP"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          value={subnetFilter}
          onChange={(e) => setSubnetFilter(e.target.value)}
          aria-label="Filter by subnet"
        >
          <option value="">All subnets</option>
          {subnetNames.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {anyFilter && <button className="mini-btn" onClick={clearFilters}>Clear filters</button>}
        <span className="filter-count">
          {matching.length} of {peers.length}
        </span>
      </div>

      {peers.length === 0 ? (
        <EmptyState
          icon="cluster"
          title="No employee peers"
          body="Staff laptops appear here once they enroll via the OptiEdge agent."
        />
      ) : groups.length === 0 ? (
        <p className="muted">
          No peer matches these filters.{' '}
          {anyFilter && (
            <button
              className="mini-btn"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </p>
      ) : (
        groups.map(([subnet, rows]) => (
          <PeerGroup key={subnet} subnet={subnet} rows={rows} sort={sort} />
        ))
      )}

      <DevicePeersTable />
    </div>
  );
}

/**
 * One subnet's peers, paged independently.
 *
 * A separate component rather than inline in the map because each group needs its
 * own `usePaged` state, and hooks cannot be called in a loop. That page number
 * stays LOCAL rather than in the URL: several groups render at once and they
 * would all write the single `?page=` key, so paging one would page them all.
 * The shared `sort` is safe to hoist — it means the same thing in every group.
 */
function PeerGroup({
  subnet,
  rows,
  sort,
}: {
  subnet: string;
  rows: Peer[];
  sort: ReturnType<typeof useUrlSort>;
}) {
  const sorted = compareBy(rows, (p) => SORT_PICK[sort.key]?.(p), sort.dir);
  const pg = usePaged(sorted, PEERS_PER_PAGE);
  return (
    // NOTE: `.section` is a HEADING class (it carries text-transform: uppercase
    // + a bottom border). It must go on the <h3>, never on a wrapping element —
    // as a container it inherits uppercase into every table cell below it, which
    // is unreadable for emails and hostnames.
    <div>
      <h3 className="section">
        {subnet}{' '}
        <span className="muted" style={{ fontWeight: 400 }}>
          · {rows.length} {rows.length === 1 ? 'peer' : 'peers'}
        </span>
      </h3>
      {/* Plain `table` (6px cell padding) rather than `.data-list--primary`
          (12px) — the looser style pushed most rows below the fold. */}
      <div className="operations-table-scroll" tabIndex={0} role="region" aria-label="VPN peer records"><table className="table-dense">
        <thead>
          <tr>
            <SortHeader label="Machine" sortKey="machine" sort={sort} />
            <SortHeader label="Owner" sortKey="ownerEmail" sort={sort} />
            <SortHeader label="VPN IP" sortKey="vpnIp" sort={sort} />
            <SortHeader label="Region" sortKey="region" sort={sort} />
            <SortHeader
              label="Last heartbeat"
              sortKey="lastHandshakeAt"
              sort={sort}
              title="Sort by when the peer last handshook"
            />
            <SortHeader label="Enrolled" sortKey="createdAt" sort={sort} />
          </tr>
        </thead>
        <tbody>
          {pg.pageItems.map((p) => (
            <tr key={p._id}>
              {/* full "owner:hostname" on hover; the column shows the machine
                  only, since Owner sits right beside it */}
              <td title={p.name}><span className="operations-truncate">{machineName(p)}</span></td>
              <td className="muted" title={p.ownerEmail ?? undefined}><span className="operations-truncate">{p.ownerEmail ?? '—'}</span></td>
              <td>
                <span className="ip-cell">{p.vpnIp}</span>
              </td>
              <td>
                <span className="badge">{p.region}</span>
              </td>
              <td>
                <Heartbeat at={p.lastHandshakeAt} />
              </td>
              <td className="muted text-xs">{formatDate(p.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <Pager
        page={pg.page}
        pageCount={pg.pageCount}
        total={pg.total}
        start={pg.start}
        pageSize={pg.pageSize}
        onPage={pg.setPage}
      />
    </div>
  );
}

// Device peers page SERVER-side (offset/limit), so this is the query page size,
// not a client slice — kept equal to the employee page size for consistency.
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Device (signage) peers — SERVER-paginated against the fleet (tens of thousands
 * of rows), so this deliberately does NOT use the client-side `usePaged`. The
 * page number and the search term still live in the URL under `dpage` / `dq`, so
 * the view is shareable; only the slicing stays on the server.
 *
 * Search matches device UUID or VPN IP via a backend case-insensitive regex.
 * Typing is debounced into the URL rather than written per keystroke — each
 * change is a network round-trip against Mongo.
 */
function DevicePeersTable() {
  // `dpage`, not `page` — a new search must reset THIS table's page number, and
  // the employee groups above own `page`.
  const [search, setSearch] = useUrlState('dq', '', 'dpage');
  const [dpage, setDpage] = useUrlNumber('dpage', 1);
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
    // `setSearch` is stable per key; re-running on it would restart the timer.
  }, [searchInput, setSearch]);

  const offset = (dpage - 1) * PAGE_SIZE;
  const { data, loading, error } = useQuery<{
    vpnDevicePeers: { items: Peer[]; total: number };
  }>(VPN_DEVICE_PEERS, {
    variables: {
      filter: search ? { search } : undefined,
      pagination: { offset, limit: PAGE_SIZE },
    },
    fetchPolicy: 'cache-and-network',
  });

  const items = data?.vpnDevicePeers.items ?? [];
  const total = data?.vpnDevicePeers.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + items.length, total);

  return (
    <div>
      {/* `.section` on the heading only — see the note in VpnPeersPage. */}
      <h3 className="section">
        Device peers{' '}
        <span className="muted" style={{ fontWeight: 400 }}>
          · {total} total
        </span>
      </h3>

      <div className="filter-row">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search device peers" placeholder="Search device UUID or VPN IP"
        />
      </div>

      {error ? (
        <>
          <p className="error">Couldn&apos;t load the device peers.</p>
          <p className="muted">
            The employee peers above come from the same source, so if they loaded this is likely
            the fleet query timing out. Retry, then ask in #devops-infra.
          </p>
        </>
      ) : (
        <>
          <div className="operations-table-scroll" tabIndex={0} role="region" aria-label="VPN peer records"><table className="table-dense">
            <thead>
              <tr>
                {/* No SortHeader: the server returns one page at a time, so
                    sorting here would only reorder the 20 rows in hand and read
                    as a broken sort. Narrow with search instead. */}
                <th>Device</th>
                <th>VPN IP</th>
                <th>Region</th>
                <th>Enrolled</th>
                <th>Last handshake</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted">
                    Loading devices…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted">
                    {search ? 'No devices match your search.' : 'No device peers yet.'}
                  </td>
                </tr>
              ) : (
                items.map((p) => (
                  <tr key={p._id}>
                    <td>
                      <div>{p.deviceName || <span className="muted">(unnamed)</span>}</div>
                      <div className="muted text-2xs">{p.name}</div>
                    </td>
                    <td>
                      <span className="ip-cell">{p.vpnIp}</span>
                    </td>
                    <td>
                      <span className="badge">{p.region}</span>
                    </td>
                    <td className="muted text-xs">{formatDate(p.createdAt)}</td>
                    <td>
                      <Heartbeat at={p.lastHandshakeAt} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table></div>

          {total > PAGE_SIZE && (
            <div className="pager">
              <span className="muted">
                {from}–{to} of {total}
              </span>
              <div className="pager-btns">
                <button
                  className="mini-btn"
                  disabled={dpage <= 1 || loading}
                  onClick={() => setDpage(dpage - 1)}
                >
                  ‹ Prev
                </button>
                <span className="muted">
                  Page {dpage} / {pageCount}
                </span>
                <button
                  className="mini-btn"
                  disabled={dpage >= pageCount || loading}
                  onClick={() => setDpage(dpage + 1)}
                >
                  Next ›
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
